import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  CreditCard, Users, TrendUp, Warning, CheckCircle, XCircle,
  Clock, ArrowsClockwise, User, Pencil
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const planLabels = { '1x': '1x/semana', '2x': '2x/semana', '3x': '3x/semana' };
const planPrices = { '1x': 90, '2x': 170, '3x': 200 };

const statusConfig = {
  active: { label: 'Ativa', color: 'badge-success', icon: CheckCircle },
  canceling: { label: 'Cancelando', color: 'badge-warning', icon: Clock },
  payment_failed: { label: 'Falha pgto', color: 'badge-error', icon: Warning },
  canceled: { label: 'Cancelada', color: 'badge-error', icon: XCircle },
  none: { label: 'Sem assinatura', color: 'bg-[#F5F2EB] text-[#73706A]', icon: Clock }
};

export default function Subscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentStudent, setCurrentStudent] = useState(null);
  const [newPlan, setNewPlan] = useState('1x');

  const fetchData = async () => {
    try {
      const [subsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/subscriptions`, { withCredentials: true }),
        axios.get(`${API}/admin/subscriptions/stats`, { withCredentials: true })
      ]);
      setSubscriptions(subsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleEditPlan = (student) => {
    setCurrentStudent(student);
    setNewPlan(student.plan);
    setEditDialogOpen(true);
  };

  const handleSavePlan = async () => {
    try {
      await axios.put(`${API}/admin/subscriptions/${currentStudent.id}/plan`, { plan: newPlan }, { withCredentials: true });
      toast.success('Plano atualizado');
      setEditDialogOpen(false);
      fetchData();
    } catch { toast.error('Erro ao atualizar plano'); }
  };

  const [sortMode, setSortMode] = useState('name-asc');
  const preFiltered = filter === 'all'
    ? subscriptions
    : subscriptions.filter(s => s.subscription_status === filter);
  const filteredSubs = [...preFiltered].sort((a, b) => {
    if (sortMode === 'name-desc') return (b.student_name || b.name || '').localeCompare(a.student_name || a.name || '', 'pt-BR');
    return (a.student_name || a.name || '').localeCompare(b.student_name || b.name || '', 'pt-BR');
  });

  const getStatusInfo = (status) => statusConfig[status] || statusConfig.none;

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="subscriptions-page">
        {/* Header */}
        <div>
          <h1 className="page-title">Assinaturas</h1>
          <p className="text-[#73706A] mt-1">Gerenciamento de assinaturas recorrentes</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="stat-card text-center">
              <p className="text-2xl font-bold font-['Outfit'] text-[#1A1A1A]">{stats.total_students}</p>
              <p className="text-xs text-[#73706A]">Alunos</p>
            </div>
            <div className="stat-card text-center cursor-pointer" onClick={() => setFilter('active')}>
              <p className="text-2xl font-bold font-['Outfit'] text-[#2D6A4F]">{stats.active_subscriptions}</p>
              <p className="text-xs text-[#73706A]">Assinaturas Ativas</p>
            </div>
            <div className="stat-card text-center cursor-pointer" onClick={() => setFilter('canceling')}>
              <p className="text-2xl font-bold font-['Outfit'] text-[#FFB703]">{stats.canceling}</p>
              <p className="text-xs text-[#73706A]">Cancelando</p>
            </div>
            <div className="stat-card text-center cursor-pointer" onClick={() => setFilter('payment_failed')}>
              <p className="text-2xl font-bold font-['Outfit'] text-[#9B2226]">{stats.payment_failed}</p>
              <p className="text-xs text-[#73706A]">Falha Pgto</p>
            </div>
            <div className="stat-card text-center cursor-pointer" onClick={() => setFilter('none')}>
              <p className="text-2xl font-bold font-['Outfit'] text-[#73706A]">{stats.no_subscription}</p>
              <p className="text-xs text-[#73706A]">Sem Assinatura</p>
            </div>
            <div className="stat-card text-center bg-[#E85D04]/5 border-[#E85D04]">
              <p className="text-2xl font-bold font-['Outfit'] text-[#E85D04]">
                ${stats.total_mrr.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-[#73706A]">MRR (Receita Mensal)</p>
            </div>
          </div>
        )}

        {/* Plan Breakdown */}
        {stats && (
          <div className="grid grid-cols-3 gap-4">
            {['1x', '2x', '3x'].map(plan => (
              <div key={plan} className="bg-white border border-[#E6E2D8] rounded-lg p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-[#1A1A1A]">{planLabels[plan]}</span>
                  <span className="text-[#E85D04] font-bold font-['Outfit']">${planPrices[plan]}/mês</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#73706A]">{stats.plan_counts[plan] || 0} assinantes</span>
                  <span className="font-medium text-[#2D6A4F]">
                    ${(stats.plan_revenue[plan] || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Filter */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-[#73706A]">Filtrar:</span>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48" data-testid="sub-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Ativas</SelectItem>
              <SelectItem value="canceling">Cancelando</SelectItem>
              <SelectItem value="payment_failed">Falha no Pagamento</SelectItem>
              <SelectItem value="none">Sem Assinatura</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => setFilter('all')}>Limpar</Button>
          <div className="ml-auto flex items-center gap-1 bg-[#F5F2EB] rounded-lg p-1" data-testid="subs-sort-controls">
            <button onClick={() => setSortMode('name-asc')} className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sortMode === 'name-asc' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A]'}`} data-testid="subs-sort-asc">A-Z</button>
            <button onClick={() => setSortMode('name-desc')} className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sortMode === 'name-desc' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A]'}`} data-testid="subs-sort-desc">Z-A</button>
          </div>
        </div>

        {/* Subscriptions Table */}
        <div className="bg-white border border-[#E6E2D8] rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-[#E85D04] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : filteredSubs.length === 0 ? (
            <div className="p-8 text-center text-[#73706A]">Nenhum resultado</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#F5F2EB]">
                    <th className="table-header">Aluno</th>
                    <th className="table-header">Plano</th>
                    <th className="table-header">Valor</th>
                    <th className="table-header">Status Assinatura</th>
                    <th className="table-header text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubs.map((sub, index) => {
                    const statusInfo = getStatusInfo(sub.subscription_status);
                    const StatusIcon = statusInfo.icon;
                    return (
                      <tr key={sub.id} className="hover:bg-[#F5F2EB]/50 transition-colors" data-testid={`sub-row-${index}`}>
                        <td className="table-cell">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#0077B6] rounded-full flex items-center justify-center">
                              <User size={18} className="text-white" />
                            </div>
                            <div>
                              <p className="font-medium text-[#1A1A1A]">{sub.name}</p>
                              <p className="text-sm text-[#73706A]">{sub.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="table-cell">
                          <span className="font-medium text-[#1A1A1A]">{sub.plan_label}</span>
                        </td>
                        <td className="table-cell">
                          <span className="font-semibold text-[#E85D04]">${sub.plan_price}/mês</span>
                        </td>
                        <td className="table-cell">
                          <span className={`badge ${statusInfo.color} flex items-center gap-1 w-fit`}>
                            <StatusIcon size={12} weight="fill" />
                            {statusInfo.label}
                          </span>
                        </td>
                        <td className="table-cell text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditPlan(sub)}
                            className="text-[#0077B6] hover:bg-[#F5F2EB]"
                            data-testid={`edit-plan-${index}`}
                          >
                            <Pencil size={18} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit Plan Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">Alterar Plano</DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <p className="text-[#73706A]">Aluno: <strong>{currentStudent?.name}</strong></p>
            <div>
              <Label>Novo Plano</Label>
              <Select value={newPlan} onValueChange={setNewPlan}>
                <SelectTrigger data-testid="edit-plan-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1x">1x/semana - $90/mês</SelectItem>
                  <SelectItem value="2x">2x/semana - $170/mês</SelectItem>
                  <SelectItem value="3x">3x/semana - $200/mês</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSavePlan} className="bg-[#E85D04] hover:bg-[#DC2F02]" data-testid="save-plan-btn">
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
